import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import CloseButton from "../../../../../components/CloseButton";
import CustomCheckbox from "../../../../../components/CustomCheckbox";
import AssignmentRoutinePreview from "../../../../../components/AssignmentRoutinePreview";
import { showDate, showGrade } from "../../../../../utils/textUtils";

/**
 * ── 변경사항 (ECHO 톤) ──
 * 1. 3단 레이아웃 → 1단 세로. 모바일 그대로 대응.
 * 2. 학년 칩 + 클래스 칩 "둘 다" 다중 선택 가능 (AND 필터).
 *    - 학년: 아무것도 안 누르면 전체 / 누른 것만 표시
 *    - 클래스: 아무것도 안 누르면 전체 / 누른 것만 표시 (미배정 포함)
 *    - 기존 FilterPanel의 gradeList/levelList AND 로직과 동일
 * 3. 선택된 학생 = 상단 잉크 태그. 태그 X로 즉시 해제.
 * 4. 출제 직전 "학생에게 이렇게 출제됩니다" 루틴 미리보기 표시.
 */

const GRADE_CHIPS = [
  { label: "초1", g: 1 }, { label: "초2", g: 2 }, { label: "초3", g: 3 },
  { label: "초4", g: 4 }, { label: "초5", g: 5 }, { label: "초6", g: 6 },
  { label: "중1", g: 7 }, { label: "중2", g: 8 }, { label: "중3", g: 9 },
  { label: "고1", g: 10 }, { label: "고2", g: 11 }, { label: "고3", g: 12 },
];

const StudentSelectionPage = ({
  onClose,
  onClickPrev,
  handleAddStudents,
  selectedStudents,
  setSelectedStudents,
  selectedDate,
  // 체험 출제일 때만 넘어온다. 재원생 대신 가상 학생 한 명만 고를 수 있다.
  demoStudentList,
  // 루틴 미리보기용 (없으면 미리보기 생략)
  worksheet,
  selectedTypes,
  selectedParagraphs,
  isRetryEnabled,
  isAutoRetryEnabled,
  maxRetries,
}) => {
  const { allStudents, levels } = useSelector((state) => state.student);

  const isDemo = Boolean(demoStudentList);
  const studentSource = demoStudentList || allStudents;

  const [keyword, setKeyword] = useState("");
  const [gradeFilter, setGradeFilter] = useState([]); // 빈 배열 = 전체
  const [levelFilter, setLevelFilter] = useState([]); // 빈 배열 = 전체, 0 = 미배정
  const [showRoutine, setShowRoutine] = useState(true);

  const toggleInList = (list, setList, value) => {
    setList(
      list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
    );
  };

  // ── 필터: 이름 AND 학년 AND 클래스 ──
  const filteredStudents = useMemo(() => {
    let list = studentSource;

    if (keyword.trim()) {
      list = list.filter((s) => s.name.includes(keyword.trim()));
    }
    if (gradeFilter.length > 0) {
      list = list.filter((s) => gradeFilter.includes(s.grade));
    }
    if (levelFilter.length > 0) {
      list = list.filter((s) =>
        levelFilter.some((levelId) => {
          if (levelId === 0) return s.level_list.length === 0;
          return s.level_list.some((l) => l.id === levelId);
        })
      );
    }
    return list;
  }, [studentSource, keyword, gradeFilter, levelFilter]);

  const toggleStudent = (student) => {
    setSelectedStudents((prev) =>
      prev.some((s) => s.id === student.id)
        ? prev.filter((s) => s.id !== student.id)
        : [...prev, student]
    );
  };

  const isAllSelected =
    filteredStudents.length > 0 &&
    filteredStudents.every((s) =>
      selectedStudents.some((sel) => sel.id === s.id)
    );

  const toggleAll = () => {
    if (isAllSelected) {
      const ids = new Set(filteredStudents.map((s) => s.id));
      setSelectedStudents((prev) => prev.filter((s) => !ids.has(s.id)));
    } else {
      setSelectedStudents((prev) => {
        const existing = new Set(prev.map((s) => s.id));
        return [...prev, ...filteredStudents.filter((s) => !existing.has(s.id))];
      });
    }
  };

  const disabled = selectedStudents.length === 0;

  return (
    <div
      
      style={{
        position: "relative",
        width: "100%",
        
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        maxHeight: "calc(80vh - 62px)",
        overflow: "hidden",
      }}
    >
      <CloseButton onClick={onClose} />

      {/* 헤더 */}
      <div style={{ padding: "22px 22px 0" }}>
        <div className="ws-modal-title">과제를 보낼 학생 선택</div>
        <p className="ws-modal-sub">
          {isDemo
            ? `마감 ${showDate(selectedDate)} · 체험이라 가상 학생에게만 나갑니다.`
            : `마감 ${showDate(selectedDate)} · 학년과 클래스를 함께 걸 수 있어요.`}
        </p>
      </div>

      {/* 스크롤 영역 */}
      <div
        className="ws-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "14px 22px 0",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {/* 체험 출제는 고를 학생이 가상 학생 한 명뿐이라 필터와 검색을 걸지 않는다. */}
        {isDemo && (
          <p className="ws-modal-demo-notice">
            체험용 학생이라 실제 재원생에게는 나가지 않고 알림톡도 발송되지 않습니다.
            출제하면 학습 링크를 알려드립니다. 직접 풀어보시면 채점과 해설지까지
            실제와 똑같이 동작합니다.
          </p>
        )}

        {!isDemo && (
        <>
        {/* 학년 필터 */}
        <div
          style={{
            fontSize: "0.72rem",
            fontWeight: 800,
            letterSpacing: "0.06em",
            color: "#7A8490",
            marginBottom: "6px",
          }}
        >
          학년{" "}
          {gradeFilter.length > 0 && (
            <button
              onClick={() => setGradeFilter([])}
              style={clearBtnStyle}
            >
              초기화
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", marginBottom: "12px" }}>
          {GRADE_CHIPS.map(({ label, g }) => (
            <button
              key={g}
              className={`ws-filter-chip${gradeFilter.includes(g) ? " active" : ""}`}
              onClick={() => toggleInList(gradeFilter, setGradeFilter, g)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* 클래스 필터 */}
        <div
          style={{
            fontSize: "0.72rem",
            fontWeight: 800,
            letterSpacing: "0.06em",
            color: "#7A8490",
            marginBottom: "6px",
          }}
        >
          클래스{" "}
          {levelFilter.length > 0 && (
            <button
              onClick={() => setLevelFilter([])}
              style={clearBtnStyle}
            >
              초기화
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", marginBottom: "14px" }}>
          {levels.map((level) => (
            <button
              key={level.id}
              className={`ws-filter-chip${levelFilter.includes(level.id) ? " active" : ""}`}
              onClick={() => toggleInList(levelFilter, setLevelFilter, level.id)}
            >
              {level.name}
            </button>
          ))}
          <button
            className={`ws-filter-chip${levelFilter.includes(0) ? " active" : ""}`}
            onClick={() => toggleInList(levelFilter, setLevelFilter, 0)}
          >
            미배정
          </button>
        </div>

        {/* 검색 */}
        <input
          type="text"
          placeholder="학생 이름 검색"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          style={{
            width: "100%",
            padding: "11px 15px",
            border: "1.5px solid #E6EDEC",
            borderRadius: "12px",
            fontSize: "0.9rem",
            fontFamily: "inherit",
            letterSpacing: "-0.01em",
            outline: "none",
            boxSizing: "border-box",
            marginBottom: "6px",
            transition: "border-color .12s",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#00D9C0")}
          onBlur={(e) => (e.target.style.borderColor = "#E6EDEC")}
        />
        </>
        )}

        {/* 전체 선택 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "9px 2px",
            borderBottom: "1.5px solid #0E141B",
            cursor: "pointer",
          }}
          onClick={toggleAll}
        >
          {/* 줄 전체에도 toggleAll 이 걸려 있다. 체크박스를 직접 누르면 onChange 와
              줄의 onClick 이 둘 다 돌아 서로를 되돌려서, 눌러도 아무 일이 없었다.
              그래서 체크박스에서 난 클릭은 여기서 끊는다. */}
          <span onClick={(e) => e.stopPropagation()}>
            <CustomCheckbox checked={isAllSelected} onChange={toggleAll} />
          </span>
          <span
            style={{
              marginLeft: "10px",
              fontSize: "0.84rem",
              fontWeight: 800,
              color: "#3A434E",
            }}
          >
            전체 선택
            <span style={{ color: "#00A396", marginLeft: "5px" }}>
              {filteredStudents.length}명
            </span>
          </span>
        </div>

        {/* 학생 목록 */}
        {filteredStudents.map((student) => {
          const isChecked = selectedStudents.some((s) => s.id === student.id);
          return (
            <div
              key={student.id}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "10px 2px",
                borderBottom: "1px solid #ECF1F2",
                cursor: "pointer",
                backgroundColor: isChecked ? "#F9FDFC" : "transparent",
              }}
              onClick={() => toggleStudent(student)}
            >
              {/* 위와 같은 이유로 클릭을 끊는다. 줄을 눌러도, 체크박스를 눌러도
                  토글은 정확히 한 번만 돈다. */}
              <span onClick={(e) => e.stopPropagation()}>
                <CustomCheckbox
                  checked={isChecked}
                  onChange={() => toggleStudent(student)}
                />
              </span>
              <span
                style={{
                  marginLeft: "12px",
                  fontSize: "0.9rem",
                  fontWeight: isChecked ? 800 : 600,
                  color: "#0E141B",
                }}
              >
                {student.name}
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "0.76rem",
                  color: "#7A8490",
                  fontWeight: 600,
                }}
              >
                {showGrade(student.grade)}
                {" · "}
                {student.level_list.length > 0
                  ? student.level_list.map((l) => l.name).join(", ")
                  : "미배정"}
              </span>
            </div>
          );
        })}

        {filteredStudents.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "36px 0",
              color: "#B9C1C9",
              fontSize: "0.86rem",
            }}
          >
            조건에 맞는 학생이 없습니다.
          </div>
        )}

        {/* ── 출제 루틴 미리보기 ── */}
        {worksheet && (
          <div style={{ margin: "16px 0 14px" }}>
            <button
              onClick={() => setShowRoutine((v) => !v)}
              style={{
                border: "none",
                background: "none",
                fontFamily: "inherit",
                fontSize: "0.78rem",
                fontWeight: 800,
                color: "#00A396",
                cursor: "pointer",
                padding: 0,
                marginBottom: "8px",
                letterSpacing: "-0.01em",
              }}
            >
              {showRoutine ? "출제 내용 접기 ▲" : "출제 내용 확인하기 ▼"}
            </button>
            {showRoutine && (
              <AssignmentRoutinePreview
                worksheet={worksheet}
                selectedTypes={selectedTypes}
                selectedParagraphs={selectedParagraphs}
                isRetryEnabled={isRetryEnabled}
                isAutoRetryEnabled={isAutoRetryEnabled}
                maxRetries={maxRetries}
              />
            )}
          </div>
        )}
      </div>

      {/* 선택 태그 + 하단 바 */}
      <div style={{ borderTop: "1px solid #ECF1F2" }}>
        {selectedStudents.length > 0 && (
          <div style={{ padding: "10px 18px 0" }}>
            <div className="ws-selected-tags">
              {selectedStudents.map((s) => (
                <span key={s.id} className="ws-student-tag">
                  {s.name}
                  <button className="remove" onClick={() => toggleStudent(s)}>
                    ✕
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="ws-bottom-bar" style={{ borderTop: "none" }}>
          {onClickPrev && (
            <button className="ws-btn-prev" onClick={onClickPrev}>
              이전
            </button>
          )}
          <button
            className="ws-btn-next"
            disabled={disabled}
            onClick={disabled ? undefined : handleAddStudents}
          >
            {disabled
              ? "학생을 선택해주세요"
              : `${selectedStudents.length}명에게 출제하기`}
          </button>
        </div>
      </div>
    </div>
  );
};

const clearBtnStyle = {
  border: "none",
  background: "none",
  fontFamily: "inherit",
  fontSize: "0.7rem",
  fontWeight: 700,
  color: "#B9C1C9",
  cursor: "pointer",
  padding: 0,
  marginLeft: "6px",
  textDecoration: "underline",
};

export default StudentSelectionPage;
